import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

ChartJS.register(ArcElement, Tooltip, Legend);

const LoanPrepaymentCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [emiPaid, setEmiPaid] = useState(12);
  const [prepaymentAmount, setPrepaymentAmount] = useState(500000);
  const [prepaymentType, setPrepaymentType] = useState('reduce_tenure');
  const [result, setResult] = useState(null);

  const calculateEMI = (principal, rate, tenure) => {
    const monthlyRate = rate / 12 / 100;
    const months = tenure * 12;
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, months) / (Math.pow(1 + monthlyRate, months) - 1);
    return emi;
  };

  const calculateOutstanding = (principal, rate, tenure, monthsPaid) => {
    const monthlyRate = rate / 12 / 100;
    const emi = calculateEMI(principal, rate, tenure);
    let balance = principal;
    
    for (let i = 0; i < monthsPaid; i++) {
      const interest = balance * monthlyRate;
      const principalPaid = emi - interest;
      balance -= principalPaid;
    }
    return Math.max(0, balance);
  };

  const calculate = () => {
    const monthlyRate = interestRate / 12 / 100;
    const totalMonths = loanTenure * 12;
    const originalEMI = calculateEMI(loanAmount, interestRate, loanTenure);
    const outstandingPrincipal = calculateOutstanding(loanAmount, interestRate, loanTenure, emiPaid);
    
    // Calculate total interest without prepayment
    const remainingMonths = totalMonths - emiPaid;
    const totalWithoutPrepay = originalEMI * remainingMonths;
    const interestWithoutPrepay = totalWithoutPrepay - outstandingPrincipal;
    
    // After prepayment
    const newOutstanding = outstandingPrincipal - prepaymentAmount;
    
    let newEMI, newTenure, interestWithPrepay, totalWithPrepay;
    
    if (prepaymentType === 'reduce_tenure') {
      // Keep EMI same, reduce tenure
      newEMI = originalEMI;
      // Calculate new tenure
      let balance = newOutstanding;
      let months = 0;
      while (balance > 0 && months < remainingMonths) {
        const interest = balance * monthlyRate;
        const principalPaid = newEMI - interest;
        balance -= principalPaid;
        months++;
      }
      newTenure = months;
      totalWithPrepay = newEMI * newTenure;
      interestWithPrepay = totalWithPrepay - newOutstanding;
    } else {
      // Keep tenure same, reduce EMI
      newTenure = remainingMonths;
      newEMI = calculateEMI(newOutstanding, interestRate, remainingMonths / 12);
      totalWithPrepay = newEMI * newTenure;
      interestWithPrepay = totalWithPrepay - newOutstanding;
    }
    
    const interestSaved = interestWithoutPrepay - interestWithPrepay;
    const tenureSaved = remainingMonths - newTenure;
    const emiReduction = originalEMI - newEMI;

    setResult({
      originalEMI,
      outstandingPrincipal,
      newOutstanding,
      originalRemainingMonths: remainingMonths,
      newTenure,
      newEMI,
      interestWithoutPrepay,
      interestWithPrepay,
      interestSaved,
      tenureSaved,
      emiReduction,
      totalWithoutPrepay,
      totalWithPrepay,
      totalSaved: interestSaved,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Loan Prepayment Analysis', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Parameter', 'Value']],
      body: [
        ['Original Loan Amount', formatCurrencyForPDF(loanAmount)],
        ['Interest Rate', `${interestRate}% p.a.`],
        ['Original Tenure', `${loanTenure} years`],
        ['EMIs Already Paid', `${emiPaid} months`],
        ['Prepayment Amount', formatCurrencyForPDF(prepaymentAmount)],
        ['Prepayment Strategy', prepaymentType === 'reduce_tenure' ? 'Reduce Tenure' : 'Reduce EMI'],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    doc.text('Prepayment Benefits', 14, finalY);

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Description', 'Without Prepayment', 'With Prepayment']],
      body: [
        ['Outstanding Principal', formatCurrencyForPDF(result.outstandingPrincipal), formatCurrencyForPDF(result.newOutstanding)],
        ['Remaining Tenure', `${result.originalRemainingMonths} months`, `${result.newTenure} months`],
        ['EMI Amount', formatCurrencyForPDF(result.originalEMI), formatCurrencyForPDF(result.newEMI)],
        ['Total Interest Payable', formatCurrencyForPDF(result.interestWithoutPrepay), formatCurrencyForPDF(result.interestWithPrepay)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
    });

    finalY = doc.lastAutoTable.finalY + 10;
    autoTable(doc, {
      startY: finalY,
      head: [['Your Savings', 'Amount']],
      body: [
        ['Interest Saved', formatCurrencyForPDF(result.interestSaved)],
        ['Tenure Reduced', `${result.tenureSaved} months (${(result.tenureSaved / 12).toFixed(1)} years)`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [239, 68, 68] },
    });

    doc.save('Loan_Prepayment_Analysis.pdf');
  };

  const chartData = result ? {
    labels: ['Interest Saved', 'Interest Payable After Prepayment'],
    datasets: [{
      data: [result.interestSaved, result.interestWithPrepay],
      backgroundColor: ['#10b981', '#ef4444'],
      borderWidth: 0,
    }]
  } : null;

  return (
    <div className="calculator-page">
      <SEO 
        title="Loan Prepayment Calculator"
        description="Free Loan Prepayment Calculator - Calculate savings with part payment. Compare EMI reduction vs tenure reduction."
        keywords="loan prepayment calculator, part payment calculator, EMI reduction calculator, tenure reduction calculator, home loan prepayment"
        canonicalPath="/loan-prepayment"
      />
      <div className="calculator-header">
        <h1>💰 Loan Prepayment Calculator</h1>
        <p>Calculate how much you can save by making extra loan payments</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Original Loan Amount (₹)</label>
            <input
              type="number"
              value={loanAmount === '' ? '' : loanAmount}
              onChange={(e) => setLoanAmount(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Interest Rate (% p.a.)</label>
              <input
                type="number"
                step="0.1"
                value={interestRate === '' ? '' : interestRate}
                onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Loan Tenure (Years)</label>
              <input
                type="number"
                value={loanTenure === '' ? '' : loanTenure}
                onChange={(e) => setLoanTenure(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>EMIs Already Paid (Months)</label>
            <input
              type="number"
              value={emiPaid === '' ? '' : emiPaid}
              onChange={(e) => setEmiPaid(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Prepayment Amount (₹)</label>
            <input
              type="number"
              value={prepaymentAmount === '' ? '' : prepaymentAmount}
              onChange={(e) => setPrepaymentAmount(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>Prepayment Strategy</label>
            <select value={prepaymentType} onChange={(e) => setPrepaymentType(e.target.value)}>
              <option value="reduce_tenure">Reduce Loan Tenure (Keep EMI Same)</option>
              <option value="reduce_emi">Reduce EMI (Keep Tenure Same)</option>
            </select>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Savings
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <h3>Interest You'll Save</h3>
                <div className="amount">{formatCurrency(result.interestSaved)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Tenure Reduced: {result.tenureSaved} months ({(result.tenureSaved / 12).toFixed(1)} years)
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Current Outstanding</span>
                  <span className="value">{formatCurrency(result.outstandingPrincipal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">After Prepayment</span>
                  <span className="value">{formatCurrency(result.newOutstanding)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Original EMI</span>
                  <span className="value">{formatCurrency(result.originalEMI)}</span>
                </div>
                <div className="result-item">
                  <span className="label">New EMI</span>
                  <span className="value">{formatCurrency(result.newEMI)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Original Remaining Tenure</span>
                  <span className="value">{result.originalRemainingMonths} months</span>
                </div>
                <div className="result-item">
                  <span className="label">New Tenure</span>
                  <span className="value">{result.newTenure} months</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">Interest Without Prepayment</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.interestWithoutPrepay)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Interest With Prepayment</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.interestWithPrepay)}</span>
                </div>
              </div>

              {chartData && (
                <div className="chart-container">
                  <h4>Interest Breakdown</h4>
                  <Pie data={chartData} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                </div>
              )}

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your loan details to see prepayment benefits</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>💡 Prepayment Tips</h4>
        <ul>
          <li><strong>Reduce Tenure:</strong> Best if you want to be debt-free faster. Saves maximum interest.</li>
          <li><strong>Reduce EMI:</strong> Best if you want to reduce monthly burden but continue paying longer.</li>
          <li>Most banks allow prepayment of floating rate loans without penalty (RBI guideline)</li>
          <li>Fixed rate loans may have 2-3% prepayment penalty</li>
        </ul>
      </div>
    </div>
  );
};

export default LoanPrepaymentCalculator;
